
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';
import clsx from 'clsx';

interface TutorialVideo {
    id: string;
    title: string;
    duration: string;
    thumbnail: string;
    description: string;
    category: 'Customization' | 'Food' | 'Products' | 'Real Estate' | 'Animals' | 'Landscapes' | 'People';
    vimeoId?: string; // Added Vimeo ID support
}

// HOW TO ADD VIDEOS: 
// Simply add your Vimeo video ID to the 'vimeoId' field below.
const TUTORIALS: TutorialVideo[] = [
    {
        id: '1',
        title: 'Backgrounds',
        duration: '2:15',
        thumbnail: 'https://images.unsplash.com/photo-1493863641943-9b68992a8d07?q=80&w=800&auto=format&fit=crop',
        description: 'Learn how to change a background, from initial upload to final high-res export.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '2',
        title: 'Props',
        duration: '4:45',
        thumbnail: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=800&auto=format&fit=crop',
        description: 'Learn how to change add, remove or change props, from initial upload to final high-res export.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '3',
        title: 'Posing',
        duration: '3:30',
        thumbnail: 'https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?q=80&w=800&auto=format&fit=crop',
        description: 'Discover how to perfectly integrate AI-generated poses into your scenes.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '4',
        title: 'Cameras',
        duration: '2:50',
        thumbnail: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=800&auto=format&fit=crop',
        description: 'Let the AI Director analyze your photo and suggest the perfect camera gear configuration.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '5',
        title: 'Lighting',
        duration: '5:20',
        thumbnail: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=800&auto=format&fit=crop',
        description: 'Understanding the aesthetic differences between studio lighting setups.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '6',
        title: 'Framing',
        duration: '5:20',
        thumbnail: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=800&auto=format&fit=crop',
        description: 'Control how your subject is framed in the shot using AI.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '7',
        title: 'Camera Angle',
        duration: '5:20',
        thumbnail: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=800&auto=format&fit=crop',
        description: 'Change the camera perspective for a completely different look.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '8',
        title: 'Retouching',
        duration: '5:20',
        thumbnail: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=800&auto=format&fit=crop',
        description: 'Understand how to apply professional retouching using the AI engine.',
        category: 'Customization',
        vimeoId: '1148755411'
    },
    {
        id: '9',
        title: 'Aspect Ratio',
        duration: '5:20',
        thumbnail: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=800&auto=format&fit=crop',
        description: 'Master image sizing for different platforms like Instagram or LinkedIn.',
        category: 'Customization',
        vimeoId: '1148755411'
    }
];

const CATEGORIES = ['All', 'Customization', 'Food','Products', 'Real Estate', 'Animals', 'Landscapes', 'People'];

export const TutorialsPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [activeVideo, setActiveVideo] = useState<TutorialVideo | null>(null);

    const filteredTutorials = selectedCategory === 'All' 
        ? TUTORIALS 
        : TUTORIALS.filter(t => t.category === selectedCategory);

    return (
        <div className="flex-1 bg-surface relative overflow-y-auto min-h-screen">
            <div className="absolute inset-0 pattern-grid opacity-5 pointer-events-none fixed"></div>

            <div className="max-w-6xl mx-auto px-6 py-12 relative z-10 pb-32">
                
                {/* Header */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-white/5 pb-8">
                    <div>
                        <button 
                            onClick={onBack}
                            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-4 group"
                        >
                            <ICONS.ChevronLeft className="w-3 h-3 transition-transform group-hover:-translate-x-1" />
                            Back to Studio
                        </button>
                        
                        {/* COMING SOON BADGE */}
                        <div className="mb-2">
                             <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
                                Coming Soon!
                             </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter italic">
                            Master <span className="text-zinc-600">Classes</span>
                        </h1>
                        <p className="text-xs text-zinc-500 uppercase tracking-widest mt-2">Elevate your photography with ReShooter Academy</p>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex bg-black/40 p-1 rounded-lg border border-white/5 overflow-x-auto no-scrollbar">
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={clsx(
                                    "px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded transition-all whitespace-nowrap",
                                    selectedCategory === cat ? "bg-white text-black shadow-lg" : "text-zinc-500 hover:text-zinc-300"
                                )}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </header>

                {/* Video Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredTutorials.map((video, idx) => (
                        <motion.div
                            key={video.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="group cursor-pointer"
                            onClick={() => setActiveVideo(video)}
                        >
                            {/* Thumbnail Container */}
                            <div className="relative aspect-video rounded-xl overflow-hidden mb-4 border border-white/5 bg-zinc-900">
                                <img 
                                    src={video.thumbnail} 
                                    alt={video.title} 
                                    className="w-full h-full object-cover grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                                />
                                
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />

                                {/* Play Button Overlay */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 group-hover:bg-white group-hover:text-black transition-all duration-300 scale-90 group-hover:scale-100">
                                        <ICONS.ChevronRight className="w-6 h-6 fill-current ml-0.5" />
                                    </div>
                                </div>

                                {/* Duration Badge */}
                                <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 backdrop-blur-md rounded text-[9px] font-mono text-zinc-300 border border-white/10">
                                    {video.duration}
                                </div>
                            </div>

                            {/* Info */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-zinc-600 border border-zinc-800 px-1.5 py-0.5 rounded">
                                        {video.category}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-white uppercase tracking-tight group-hover:text-zinc-300 transition-colors">
                                    {video.title}
                                </h3>
                                <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                                    {video.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Video Player Modal */}
            <AnimatePresence>
                {activeVideo && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-12"
                        onClick={() => setActiveVideo(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className="w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {activeVideo.vimeoId ? (
                                <iframe
                                    src={`https://player.vimeo.com/video/${activeVideo.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1`}
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                                    className="absolute inset-0 w-full h-full"
                                    title={activeVideo.title}
                                ></iframe>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-12">
                                    <ICONS.Refresh className="w-12 h-12 text-zinc-700 animate-spin mb-6" />
                                    <h2 className="text-2xl font-bold text-white uppercase tracking-[0.2em] mb-4">{activeVideo.title}</h2>
                                    <p className="text-zinc-500 text-sm max-w-md">Video content not available. Add a vimeoId to the tutorial data.</p>
                                </div>
                            )}

                            {/* Close Button */}
                            <button 
                                onClick={() => setActiveVideo(null)}
                                className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-white text-zinc-400 hover:text-black rounded-full transition-all border border-white/10 backdrop-blur-md z-30"
                                title="Close Player"
                            >
                                <ICONS.Close className="w-6 h-6" />
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
